import { Component, OnInit, AfterViewInit, ViewEncapsulation, ViewChild, ElementRef, TemplateRef, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import * as Feather from 'feather-icons';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ClientService } from '../../../../services/Client/client.service';
import { Client } from '../../../../models/client';
import { PaginatedResponse } from '../../../../models/paginatedResponse';
import { ClienEventService } from '../../../../services/Client/clien-event.service';
import {EmailComposeComponent} from '../../Vente/devis/parametre-bar/email-compose/email-compose.component';

@Component({
    selector: 'app-archive',
    templateUrl: './archive.component.html',
    styleUrls: ['./archive.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ArchiveComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('filterPanel') filterPanel!: ElementRef;
    @ViewChild('bankModal') bankModal!: TemplateRef<any>;
    @ViewChild('phoneModal') phoneModal!: TemplateRef<any>;
    @ViewChild('emailModal') emailComposeComponent!: EmailComposeComponent;

    clients: Client[] = [];
    filteredClients: Client[] = [];
    paginatedData: Client[] = [];
    searchTerm: string = '';
    showFilter: boolean = false;
    clientName: string = '';
    selectedClient: Client | null = null;
    basicSelectedOption: number = 10;
    currentPage: number = 1;
    totalPages: number = 1;
    totalItems: number = 0;
    pages: number[] = [];
    filterSummary: string = '';
    filters: any = {
        type: '',
        raison: '',
        matricule: '',
        chiffre_aff: '',
        operator: '=',
    };
    stationId: number = 0;
    isLoading: boolean = false;
    private subscriptions: Subscription[] = [];

    constructor(
        private modalService: NgbModal,
        private toastr: ToastrService,
        private clientService: ClientService,
        private clientEventService: ClienEventService,
        private router: Router
    ) {}

    ngOnInit() {
        this.loadUserData();
        this.resetFilters();
        this.loadClients();
        this.subscriptions.push(
            this.clientEventService.onClientArchiveEvent().subscribe(({ client, is_archive }) => {
                if (is_archive) {
                    if (!this.clients.find((c) => c.id === client.id)) {
                        this.clients.push(client);
                        this.totalItems++;
                        this.updateFilteredData();
                        this.toastr.info(`Client ${client.nom_prenom} ajouté aux archivés`, 'Info');
                    }
                } else {
                    this.clients = this.clients.filter((c) => c.id !== client.id);
                    this.totalItems--;
                    this.updateFilteredData();
                    this.toastr.info(`Client ${client.nom_prenom} retiré des archivés`, 'Info');
                }
            })
        );
    }

    ngAfterViewInit() {
        setTimeout(() => Feather.replace(), 0);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    loadUserData() {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            this.stationId = user?.stations?.[0]?.id || 0;
            if (!this.stationId) {
                this.toastr.error('Aucune station trouvée. Veuillez créer une station.', 'Erreur');
                this.router.navigate(['/create-station']);
            }
        } catch (error) {
            this.toastr.error('Erreur lors de la récupération des données utilisateur.', 'Erreur');
            this.stationId = 0;
        }
    }

    loadClients(page: number = this.currentPage, limit: number = this.basicSelectedOption) {
        if (!this.stationId) {
            this.toastr.error('ID de station invalide.', 'Erreur');
            return;
        }
        if (this.isLoading) {
            return;
        }
        this.isLoading = true;
        const sub = this.clientService.getArchivedClient(this.stationId, page, limit).subscribe({
            next: (response: PaginatedResponse<Client>) => {
                this.clients = response.data || [];
                this.totalItems = response.meta?.total || 0;
                this.totalPages = response.meta?.nb_total_pages || response.meta?.pages || Math.ceil(this.totalItems / this.basicSelectedOption) || 1;
                this.currentPage = response.meta?.page || page;
                if (this.currentPage > this.totalPages) {
                    this.currentPage = this.totalPages;
                    this.loadClients(this.currentPage, limit);
                    return;
                }
                this.filteredClients = [...this.clients];
                this.updateFilteredData();
                this.isLoading = false;
            },
            error: (error) => {
                this.isLoading = false;
                const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement des clients archivés';
                this.toastr.error(errorMessage, 'Erreur');
                this.clients = [];
                this.filteredClients = [];
                this.paginatedData = [];
                this.totalItems = 0;
                this.totalPages = 1;
                this.currentPage = 1;
                this.updatePagination();
            },
        });
        this.subscriptions.push(sub);
    }

    toggleFilter() {
        this.showFilter = !this.showFilter;
        if (this.showFilter) {
            setTimeout(() => Feather.replace(), 0);
        }
    }

    onSearch() {
        this.currentPage = 1;
        this.applyFilters();
    }

    updateFilteredData() {
        this.filteredClients = this.clients.filter((client) => {
            const searchMatch =
                (client.identifiant_unique || client.id?.toString() || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                (client.nom_prenom || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                //(client.emails || []).some(email => email.toLowerCase().includes(this.searchTerm.toLowerCase()));
            (client.emails || []).some(email => email.email?.toLowerCase().includes(this.searchTerm.toLowerCase()));
            const typeMatch = !this.filters.type || (client.type || '').toLowerCase() === this.filters.type.toLowerCase();
            const raisonMatch = !this.filters.raison || (client.nom_prenom || '').toLowerCase().includes(this.filters.raison.toLowerCase());
            const matriculeMatch = !this.filters.matricule || (client.identifiant_unique || client.id?.toString() || '').toLowerCase().includes(this.filters.matricule.toLowerCase());
            const chiffreAffMatch = !this.filters.chiffre_aff || true; // Not implemented
            return searchMatch && typeMatch && raisonMatch && matriculeMatch && chiffreAffMatch;
        });

        if (this.filters.chiffre_aff) {
            this.toastr.warning("Filtre chiffre d'affaires non implémenté (champ absent dans le modèle)", 'Avertissement');
        }

        this.updateFilterSummary();
        this.updatePagination();
    }

    updateFilterSummary() {
        const activeFilters = [];
        if (this.filters.type) {
            activeFilters.push(`Type: ${this.filters.type}`);
        }
        if (this.filters.raison) {
            activeFilters.push(`Raison: ${this.filters.raison}`);
        }
        if (this.filters.matricule) {
            activeFilters.push(`Matricule: ${this.filters.matricule}`);
        }
        if (this.filters.chiffre_aff) {
            activeFilters.push(`Chiffre: ${this.filters.operator} ${this.filters.chiffre_aff}`);
        }
        this.filterSummary = activeFilters.join(', ');
    }

    resetFilters() {
        this.filters = { type: '', raison: '', matricule: '', chiffre_aff: '', operator: '=' };
        this.searchTerm = '';
        this.currentPage = 1;
        this.loadClients();
    }

    applyFilters() {
        this.currentPage = 1;
        this.loadClients();
    }

    updatePagination() {
        this.totalPages = Math.ceil(this.totalItems / this.basicSelectedOption) || 1;
        this.currentPage = Math.min(this.currentPage, this.totalPages);
        this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
        const start = (this.currentPage - 1) * this.basicSelectedOption;
        this.paginatedData = this.filteredClients.slice(start, start + this.basicSelectedOption);
    }

    changePage(page: number) {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage && !this.isLoading) {
            this.currentPage = page;
            this.loadClients(page, this.basicSelectedOption);
        }
    }

    onLimitChange() {
        this.currentPage = 1;
        this.loadClients(1, this.basicSelectedOption);
    }

    sendEmail(client: Client) {
        window.location.href = `mailto:${client.emails && client.emails.length > 0 ? client.emails[0] : ''}`;
    }

    openPhoneModal(client: Client) {
        this.selectedClient = client;
        this.modalService.open(this.phoneModal, { centered: true });
    }

    viewItem(client: Client) {
        const clientId = client.id || client.identifiant_unique;
        if (!clientId) {
            this.toastr.error('ID de client manquant ou invalide.', 'Erreur');
            return;
        }
        this.router.navigate(['/tiers/clients/details', clientId], {
            state: { client },
        });
    }

    editItem(client: Client) {
        this.router.navigate([`/tiers/clients/edit/${client.id}`], {
            state: { client, isEditMode: true },
        });
    }

    openModal(client: Client, action: string) {
        this.clientName = client.nom_prenom || 'N/A';
        this.modalService.open(this.bankModal, { centered: true }).result.then(
            (result) => {
                if (result === 'Confirm click') {
                    this.unarchiveClient(client);
                }
            },
            () => {
                this.toastr.info('Désarchivage annulé', 'Information');
            }
        );
    }

    unarchiveClient(client: Client) {
        if (!client.id) {
            this.toastr.error('ID de client manquant pour le désarchivage.', 'Erreur');
            return;
        }
        const sub = this.clientService.archiveClient(client.id, false).subscribe({
            next: (updatedClient) => {
                this.clients = this.clients.filter((c) => c.id !== client.id);
                this.totalItems--;
                this.updateFilteredData();
                this.clientEventService.emitClientArchiveEvent(updatedClient, false);
                this.toastr.success(`Client ${client.nom_prenom} désarchivé`, 'Succès');
            },
            error: (error) => {
                const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du désarchivage';
                this.toastr.error(errorMessage, 'Erreur');
            },
        });
        this.subscriptions.push(sub);
    }
    openEmailModal(client: Client) {
        if (!client.emails || client.emails.length === 0 || !client.emails[0].email) {
            this.toastr.error('Aucune adresse e-mail disponible pour ce client.', 'Erreur');
            return;
        }
        if (!client.id) {
            this.toastr.error('ID du client manquant. Impossible d\'envoyer l\'email.', 'Erreur');
            return;
        }
        this.selectedClient = client;
        if (this.emailComposeComponent) {
            // Pré-remplir l'email du destinataire
            this.emailComposeComponent.email.to = client.emails[0].email;
            this.emailComposeComponent.openModal();
        } else {
            this.toastr.error('Erreur : Impossible d\'ouvrir le modal d\'email.', 'Erreur');
        }
    }
    clearFilter() {
        this.filterSummary = '';
        this.filters = { type: '', raison: '', matricule: '', chiffre_aff: '', operator: '=' };
        this.searchTerm = '';
        this.currentPage = 1;
        this.loadClients(this.currentPage, this.basicSelectedOption);
    }
}
